import React, { useState, useMemo, useCallback } from 'react';
import { useLeadNotifications } from '../hooks/useLeadNotifications';
import { NotificationCenter } from './NotificationCenter';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { canSeeNav, isPostventaHiddenForTenant, ROLE_LABEL_KEYS } from '../config/permissions';
import { cn } from '../lib/utils';
import { api, API_BASE_URL } from '../services/api';
import { getEnvironmentKey } from '../utils/environment';
import { ThemeToggle } from './ui/ThemeToggle';
import {
  HomeIcon,
  UserGroupIcon,
  PhoneIcon,
  ChartBarIcon,
  UsersIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MegaphoneIcon,
  WrenchScrewdriverIcon,
  GlobeAltIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  BookOpenIcon,
  LifebuoyIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

// All navigation items with section tags and i18n keys
const allNavigation = [
  // MAIN section
  { nameKey: 'nav.dashboard', href: '/', icon: HomeIcon, color: '#1d4ed8', section: 'MAIN' },
  { nameKey: 'nav.agents', href: '/agents', icon: UserGroupIcon, color: '#9333ea', section: 'MAIN' },
  // { nameKey: 'nav.tools', href: '/tools', icon: WrenchScrewdriverIcon, color: '#0891b2', section: 'MAIN' }, // hidden
  { nameKey: 'nav.campaigns', href: '/campaigns', icon: MegaphoneIcon, color: '#7c3aed', section: 'MAIN' },
  { nameKey: 'nav.calls', href: '/calls', icon: PhoneIcon, color: '#16a34a', section: 'MAIN' },
  { nameKey: 'nav.callHistory', href: '/call-history', icon: ClockIcon, color: '#8b5cf6', section: 'MAIN' },
  { nameKey: 'nav.analytics', href: '/analytics', icon: ChartBarIcon, color: '#f59e0b', section: 'MAIN' },
  { nameKey: 'nav.postventa', href: '/postventa', icon: WrenchScrewdriverIcon, color: '#0d9488', section: 'MAIN' },
  // MANAGE section
  { nameKey: 'nav.phoneNumbers', href: '/phone-numbers', icon: PhoneIcon, color: '#059669', section: 'MANAGE' },
  { nameKey: 'nav.whatsapp', href: '/whatsapp', icon: ChatBubbleLeftRightIcon, color: '#10b981', section: 'MANAGE' },
  { nameKey: 'nav.calendar', href: '/calendar', icon: CalendarDaysIcon, color: '#0d9488', section: 'MANAGE' },
  { nameKey: 'nav.team', href: '/team', icon: UsersIcon, color: '#dc2626', section: 'MANAGE' },
  // Configuración / Settings (Cadencia de contacto Postventa). Visibility per role
  // comes from NAV_PERMISSIONS['/settings'] (admin/super_admin/manager).
  { nameKey: 'nav.settings', href: '/settings', icon: Cog6ToothIcon, color: '#6b7280', section: 'MANAGE' },
  { nameKey: 'nav.docs', href: '/manual', icon: BookOpenIcon, color: '#2563eb', section: 'MANAGE' },
  { nameKey: 'nav.support', href: '/support', icon: LifebuoyIcon, color: '#0ea5e9', section: 'MANAGE' },
  // salesperson-profiles nav entry removed — superseded by agent owner_user_id
];

const isNavActive = (pathname, href) =>
  pathname === href || (href === '/campaigns' && pathname.startsWith('/campaigns/'));

function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { notifications, unreadCount, clearUnread, dismissNotification, clearAllNotifications } = useLeadNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, tenant, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
  };

  // Filter navigation items by user role, then by tenant-layout module gating.
  // Postventa is hidden for cobranza tenants (config-driven via tenant.analytics_layout).
  const filteredNav = useMemo(() => {
    if (!user) return { main: [], manage: [] };
    const hidePostventa = isPostventaHiddenForTenant(tenant);
    const visible = allNavigation.filter(item => {
      if (!canSeeNav(user.role, item.href)) return false;
      if (hidePostventa && item.href === '/postventa') return false;
      return true;
    });
    return {
      main: visible.filter(item => item.section === 'MAIN'),
      manage: visible.filter(item => item.section === 'MANAGE'),
    };
  }, [user, tenant]);

  const roleLabelKey = ROLE_LABEL_KEYS[user?.role] || 'roles.caller';
  const roleLabel = t(roleLabelKey);

  // Environment identity derived from the pinned API host (no backend call).
  // Hidden in production so the badge only flags non-prod (testing/local) UIs.
  const envKey = getEnvironmentKey(API_BASE_URL, window.location.origin);
  const showEnvBadge = envKey !== 'production' && envKey !== 'unknown';

  // Navigation item component
  const NavItem = ({ item, isActive }) => (
    <Link
      to={item.href}
      className={cn(
        'nav-item group',
        isActive && 'active',
        isCollapsed && 'justify-center px-0'
      )}
      title={isCollapsed ? t(item.nameKey) : undefined}
    >
      <item.icon
        className="icon"
        style={{ color: item.color }}
      />
      {!isCollapsed && (
        <span className="truncate">{t(item.nameKey)}</span>
      )}
    </Link>
  );

  // Section header component
  const SectionHeader = ({ titleKey }) => (
    !isCollapsed && (
      <div className="nav-section-header mt-2 mb-1">
        {t(titleKey)}
      </div>
    )
  );

  return (
    <div className="h-screen flex overflow-hidden bg-background">
      {/* Mobile sidebar overlay */}
      <div className={cn(
        'fixed inset-0 flex z-40 md:hidden',
        sidebarOpen ? '' : 'hidden'
      )}>
        <div
          className="fixed inset-0"
          style={{ backgroundColor: 'var(--overlay)' }}
          onClick={() => setSidebarOpen(false)}
        />
        <div className="relative flex-1 flex flex-col max-w-xs w-full glass-panel">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>

          {/* Mobile sidebar content */}
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <Link to="/" className="flex items-center px-4 mb-4 no-underline hover:opacity-80 transition-opacity" onClick={() => setSidebarOpen(false)}>
              <img
                src="/logo.png"
                alt="qomplement"
                className="h-8 w-8 rounded-lg object-contain"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
              <div className="hidden h-8 w-8 bg-primary rounded-lg items-center justify-center">
                <PhoneIcon className="h-5 w-5 text-white" />
              </div>
              <h1 className="ml-3 text-lg font-semibold text-foreground">
                qomplement Voice
              </h1>
            </Link>

            <nav className="px-2 space-y-1">
              <div className="nav-section-header">{t('nav.sectionMain')}</div>
              {filteredNav.main.map((item) => (
                <NavItem
                  key={item.nameKey}
                  item={item}
                  isActive={isNavActive(location.pathname, item.href)}
                />
              ))}

              {filteredNav.manage.length > 0 && (
                <>
                  <div className="nav-section-header mt-4">{t('nav.sectionManage')}</div>
                  {filteredNav.manage.map((item) => (
                    <NavItem
                      key={item.nameKey}
                      item={item}
                      isActive={isNavActive(location.pathname, item.href)}
                    />
                  ))}
                </>
              )}
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar — glass morphism */}
      <div
        className={cn(
          'hidden md:flex md:flex-col md:flex-shrink-0 transition-all duration-200 ease-out',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <div className="flex flex-col w-full h-full border-r border-border glass-panel overflow-hidden">
          {/* Sidebar header with toggle */}
          <div className="flex items-center justify-between p-3 border-b border-border">
            {!isCollapsed && (
              <Link to="/" className="flex items-center no-underline hover:opacity-80 transition-opacity">
                <img
                  src="/logo.png"
                  alt="qomplement"
                  className="h-8 w-8 rounded-lg object-contain"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
                <div className="hidden h-8 w-8 bg-primary rounded-lg items-center justify-center">
                  <PhoneIcon className="h-5 w-5 text-white" />
                </div>
                <h1 className="ml-3 text-base font-semibold text-foreground">
                  qomplement Voice
                </h1>
              </Link>
            )}
            <button
              onClick={toggleSidebar}
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                isCollapsed && 'mx-auto'
              )}
              style={{ color: 'var(--muted)' }}
              title={isCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
            >
              {isCollapsed ? (
                <ChevronRightIcon className="h-4 w-4" />
              ) : (
                <ChevronLeftIcon className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className={cn(
            'flex-1 overflow-y-auto py-2',
            isCollapsed ? 'px-2' : 'pl-2 pr-0'
          )}>
            {!isCollapsed && <SectionHeader titleKey="nav.sectionMain" />}
            <div className="space-y-0.5">
              {filteredNav.main.map((item) => (
                <NavItem
                  key={item.nameKey}
                  item={item}
                  isActive={isNavActive(location.pathname, item.href)}
                />
              ))}
            </div>

            {filteredNav.manage.length > 0 && (
              <>
                {!isCollapsed && <SectionHeader titleKey="nav.sectionManage" />}
                {isCollapsed && <div className="h-2" />}
                <div className="space-y-0.5">
                  {filteredNav.manage.map((item) => (
                    <NavItem
                      key={item.nameKey}
                      item={item}
                      isActive={isNavActive(location.pathname, item.href)}
                    />
                  ))}
                </div>
              </>
            )}
          </nav>

          {/* Sidebar footer - User info */}
          <div className="p-3 border-t border-border">
            {isCollapsed ? (
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center p-2 rounded-lg transition-colors"
                style={{ color: 'var(--muted)' }}
                title={t('nav.logout')}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0">
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--secondary)' }}
                  >
                    <span className="text-sm font-medium" style={{ color: 'var(--muted)' }}>
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="ml-2 min-w-0">
                    <p className="text-sm font-medium truncate text-foreground">
                      {user?.name}
                    </p>
                    <p className="text-xs truncate" style={{ color: 'var(--muted)' }}>
                      {roleLabel}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-1.5 rounded-lg transition-colors flex-shrink-0"
                  style={{ color: 'var(--muted)' }}
                  title={t('nav.logout')}
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar — glass morphism */}
        <div className="relative z-10 flex-shrink-0 flex h-14 glass-panel border-b border-border">
          {/* Mobile menu button */}
          <button
            type="button"
            className="px-4 md:hidden"
            style={{ color: 'var(--muted)' }}
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Page title and actions */}
          <div className="flex-1 px-4 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm font-medium" style={{ color: 'var(--muted)' }}>
                {t(allNavigation.find(item => isNavActive(location.pathname, item.href))?.nameKey || 'nav.dashboard')}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              {/* Environment badge — only shown on non-prod builds (testing/local). */}
              {showEnvBadge && (
                <span
                  data-testid="env-badge"
                  className="hidden md:inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide flex-shrink-0"
                  style={{ backgroundColor: 'var(--secondary)', color: 'var(--muted)' }}
                  title={t('settings.environment')}
                >
                  {t(`settings.env.${envKey}`)}
                </span>
              )}

              {/* Tenant info */}
              {tenant && (
                <div className="hidden md:flex items-center text-sm flex-shrink-0 max-w-[180px]" style={{ color: 'var(--muted)' }}>
                  <BuildingOfficeIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                  <span className="truncate">{tenant.business_name}</span>
                </div>
              )}

              {/* Theme toggle */}
              <ThemeToggle />

              {/* Language toggle */}
              <button
                type="button"
                onClick={toggleLanguage}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-colors text-sm font-medium"
                style={{ color: 'var(--muted)' }}
                title={i18n.language === 'en' ? 'Cambiar a Espanol' : 'Switch to English'}
              >
                <GlobeAltIcon className="h-4 w-4" />
                <span>{i18n.language === 'en' ? 'EN' : 'ES'}</span>
              </button>

              {/* Notification center */}
              <NotificationCenter
                notifications={notifications}
                unreadCount={unreadCount}
                onOpen={clearUnread}
                onDismiss={dismissNotification}
                onClearAll={clearAllNotifications}
                onNotificationClick={async (n) => {
                  clearUnread();
                  if (!n) { navigate('/campaigns'); return; }
                  let campaignId = n.campaign_id;
                  let contactId = n.contact_id;
                  const phone = n.phone || null;
                  // Backfill: older/cached notifications (and calls-table fallback items
                  // synthesized before #747) may lack campaign_id/contact_id. Resolve via
                  // call_sid so the click still deep-links instead of dead-ending.
                  if ((!campaignId || !contactId) && n.call_sid) {
                    try {
                      const { data } = await api.get(`/calls/${n.call_sid}/detail`);
                      campaignId = campaignId || data?.campaign_id;
                      contactId = contactId || data?.contact_id;
                    } catch (err) {
                      console.warn('[NOTIF] deep-link backfill failed for call_sid', n.call_sid, err?.message || err);
                    }
                  }
                  if (campaignId) {
                    // Prefer contact_id; if still missing, hand the phone to CampaignDetail
                    // so it can match the lead row by phone instead of dead-ending on the list.
                    // Send BOTH when available: a stale/legacy contact_id that no
                    // longer matches the calls-table row would otherwise dead-click.
                    // Passing focusPhone alongside lets CampaignDetail fall back to
                    // phone-normalized matching instead of dead-ending.
                    const qs = new URLSearchParams();
                    if (contactId) qs.set('focusContact', contactId);
                    if (phone) qs.set('focusPhone', phone);
                    const suffix = qs.toString() ? `?${qs.toString()}` : '';
                    navigate(`/campaigns/${campaignId}${suffix}`);
                  } else {
                    console.warn('[NOTIF] notification click had no resolvable campaign_id; opening campaign list', { call_sid: n.call_sid, phone });
                    navigate('/campaigns');
                  }
                }}
              />

              {/* User menu (desktop) */}
              <div className="hidden md:flex items-center">
                <div className="mr-3 text-right">
                  <p className="text-sm font-medium text-foreground">
                    {user?.name}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    {roleLabel}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg transition-colors"
                  title={t('nav.logout')}
                  style={{ color: 'var(--muted)' }}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none bg-background">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;
